import { Suspense, lazy } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';

const Waitlist = lazy(() => import('../components/Waitlist'));

const CANONICAL = 'https://www.eclipsegroup.in/corporate-travel';

// What Voyara AI is built to do, in the same terms as the rest of the site.
// No performance figures: it is in private early access.
const USE_CASES = [
  {
    heading: 'Travellers on the road',
    body: 'When a flight is late or cancelled, Voyara AI is built to re-plan the onward flight, the hotel and the car together, and to tell the traveller once what changed instead of leaving them to call each provider.',
  },
  {
    heading: 'Travel managers and assistants',
    body: 'The trip lives in one plan, so whoever arranges travel sees the same up-to-date itinerary as the traveller rather than piecing it together from confirmation emails.',
  },
  {
    heading: 'Multi-city trips and roadshows',
    body: 'On itineraries that cross several cities and time zones, a change early in the trip is checked against everything that follows it, from connections to the next morning’s meetings.',
  },
  {
    heading: 'Policy and finance',
    body: 'Voyara AI is built to propose changes within the company’s travel policy and to keep the trip record complete for expense reconciliation once the traveller is home.',
  },
];

const STEPS = [
  {
    heading: 'Start from the whole trip',
    body: 'Voyara AI reads the trip as one plan: flights, hotels, ground transport and the meetings they serve.',
  },
  {
    heading: 'Watch for friction',
    body: 'It follows flight status, connections and conditions on the ground, looking for the points where a delay would knock on to the rest of the plan.',
  },
  {
    heading: 'Re-plan together',
    body: 'When something changes, it proposes one set of changes across every booking it affects, rather than one fix at a time.',
  },
  {
    heading: 'Keep everyone in step',
    body: 'The traveller and the people who arrange their travel see the same updated plan.',
  },
];

const FAQS = [
  {
    question: 'What is Voyara AI for corporate travel?',
    answer:
      'Voyara AI is a travel-coordination platform built by Eclipse Group. For companies, it is built to keep employees’ flights, hotels, ground transport and meetings in step after booking, re-planning them together when a trip changes.',
  },
  {
    question: 'Is Voyara AI available now?',
    answer:
      'Voyara AI is in private early access. Companies can request access with the form on this page.',
  },
  {
    question: 'Does Voyara AI replace our travel management company or booking tool?',
    answer:
      'Voyara AI is built as a coordination layer across the whole trip rather than another booking screen. How it fits alongside an existing travel management company or online booking tool depends on your programme, so tell us about it when you request access.',
  },
  {
    question: 'Who builds Voyara AI?',
    answer:
      'Voyara AI is built by Eclipse Group, the trading name of Eclipse Private Limited, an Indian technology company.',
  },
];

const FURTHER_READING = [
  { to: '/travel-intelligence', label: 'How Voyara AI travel intelligence works' },
  { to: '/knowledge/business-travel-orchestration-2026', label: 'Business travel orchestration: the enterprise guide' },
  { to: '/knowledge/online-booking-tool-obt', label: 'Online booking tools (OBT) in business travel' },
  { to: '/knowledge/virtual-credit-cards', label: 'Virtual cards for business travel' },
  { to: '/destinations/business-travel-orchestration', label: 'A New York, London and Dubai trip as one plan' },
];

const LINK_CLASS = 'text-blue-400 hover:text-blue-300 underline underline-offset-4 decoration-blue-400/30';

export default function CorporateTravel() {
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${CANONICAL}#webpage`,
        "url": CANONICAL,
        "name": "Corporate Travel & Early Access | Voyara AI",
        "description": "How Voyara AI is built to coordinate company travel after booking, and how to request early access.",
        "about": { "@id": "https://www.eclipsegroup.in/#software" }
      },
      {
        "@type": "FAQPage",
        "@id": `${CANONICAL}#faq`,
        "mainEntity": FAQS.map((item) => ({
          "@type": "Question",
          "name": item.question,
          "acceptedAnswer": { "@type": "Answer", "text": item.answer }
        }))
      }
    ]
  };

  return (
    <main className="relative z-10 pt-20">
      <SEO
        title="Corporate Travel & Early Access — Voyara AI"
        description="Voyara AI is built to keep company trips in step after booking: flights, hotels, ground transport and meetings re-planned together. Request early access."
        canonical={CANONICAL}
        schema={schema}
      />

      <div className="max-w-3xl mx-auto px-6 pt-12">
        <Breadcrumbs trail={[{ name: 'Corporate Travel', path: '/corporate-travel' }]} />

        <h1 className="font-serif text-[clamp(2.5rem,5vw,4.5rem)] leading-[1.05] tracking-tight mb-6">
          Corporate travel, <span className="italic text-white/70">kept in step.</span>
        </h1>
        <p className="text-white/70 text-lg leading-relaxed mb-16">
          Voyara AI for corporate travel is built to look after a company’s trips once they are booked: flights,
          hotels, ground transport and the meetings they serve, re-planned together when something changes. It is
          in private early access from Eclipse Group, and companies can request access below.
        </p>

        <section aria-labelledby="use-cases" className="mb-16">
          <h2 id="use-cases" className="font-serif text-2xl md:text-3xl mb-8">Where it helps</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {USE_CASES.map((useCase) => (
              <div key={useCase.heading} className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.05]">
                <h3 className="font-sans text-primary font-medium mb-3">{useCase.heading}</h3>
                <p className="text-secondary text-sm leading-relaxed">{useCase.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section aria-labelledby="how-it-works" className="mb-16">
          <h2 id="how-it-works" className="font-serif text-2xl md:text-3xl mb-8">How it works</h2>
          <ol className="space-y-6 list-none p-0 m-0">
            {STEPS.map((step, index) => (
              <li key={step.heading} className="flex gap-5">
                <span className="text-blue-400/70 font-serif text-2xl leading-none w-6 shrink-0" aria-hidden="true">
                  {index + 1}
                </span>
                <div>
                  <h3 className="font-sans text-primary font-medium mb-1.5">{step.heading}</h3>
                  <p className="text-secondary leading-relaxed">{step.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section aria-labelledby="faq" className="mb-16">
          <h2 id="faq" className="font-serif text-2xl md:text-3xl mb-8">Frequently asked questions</h2>
          <div className="space-y-6">
            {FAQS.map((item) => (
              <div key={item.question} className="p-6 rounded-xl bg-white/[0.02] border border-white/5">
                <h3 className="text-lg font-medium text-white mb-3">{item.question}</h3>
                <p className="text-secondary leading-relaxed text-sm">{item.answer}</p>
              </div>
            ))}
          </div>
        </section>

        <nav aria-label="Further reading">
          <h2 className="font-serif text-2xl md:text-3xl mb-6">Further reading</h2>
          <ul className="space-y-3">
            {FURTHER_READING.map((link) => (
              <li key={link.to}>
                <Link to={link.to} className={LINK_CLASS}>{link.label}</Link>
              </li>
            ))}
          </ul>
        </nav>
      </div>

      <Suspense fallback={<div className="h-64 flex items-center justify-center"><div className="w-4 h-4 rounded-full border-2 border-white/10 border-t-white/50 animate-spin" /></div>}>
        <Waitlist />
      </Suspense>
    </main>
  );
}
